/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FormProvider, useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { Element } from 'react-scroll';

import {
  PrimaryLoadingButton, SecondaryButton,
  StickyHeader, StickySidebar, WhiteBgCard, Loader,
} from 'components';
import {
  selectAchievements, selectAuthUser, selectBadges,
  selectLoader,
  selectSponsorContent, selectStatistics,
} from 'base/auth/selectors';
import { useConfirmation } from 'base/common';
import { editProfileActions } from './slice';

import { IUpdateProfileForm } from './interface';
import updateProfileFormValidation from './validation/update-profile-form-validation';
import GeneralInformationForm from './components/general-information-form';
import BankAccountDetailsForm from './components/bank-account-details-form';

import './styles.scss';
import SponsorsLogoForm from './components/sponsor-logo-form';
import AchievementsForm from './components/achievements-form';
import StatisticsForm from './components/statistics-form';
import BadgeForm from './components/badges-form';

const ProfileUpdate = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isNewProfile, setIsNewProfile] = React.useState(false);

  React.useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const newProfile = queryParams.get('newProfile');
    setIsNewProfile(newProfile !== null && newProfile === 'true');
  }, []);

  const authUser = useSelector(selectAuthUser);
  const sponsors = useSelector(selectSponsorContent);
  const achievements = useSelector(selectAchievements);
  const statistics = useSelector(selectStatistics);
  const badges = useSelector(selectBadges);
  const loading = useSelector(selectLoader);
  const confirm = useConfirmation();

  const methods = useForm<IUpdateProfileForm>({
    defaultValues: {
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      email: authUser.email,
      profileImgUrl: authUser.profileImgUrl,
      bio: authUser.bio,
      category: authUser.attributes.category,
      currency: authUser.attributes.currency,
      introVideoUrl: authUser.attributes.introVideoUrl,
      thankVideoUrl: authUser.attributes.thankVideoUrl,
      rateVideoUrl: authUser.attributes.rateVideoUrl,
      accountType: authUser.attributes.accountType,
      password: authUser.attributes.password,
      subscriptionFee: authUser.attributes.subscriptionFee,
      subscriptionFrequency: authUser.attributes.subscriptionFrequency,
      sponsors,
      achievements,
      statistics,
      badges,
      categories: authUser.categories.map((i) => i.id),
    },
    resolver: yupResolver(updateProfileFormValidation),
    shouldFocusError: true,
  });

  const resetStateChanges = () => {
    methods.reset({
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      email: authUser.email,
      profileImgUrl: authUser.profileImgUrl,
      bio: authUser.bio,
      category: authUser.attributes.category,
      currency: authUser.attributes.currency,
      introVideoUrl: authUser.attributes.introVideoUrl,
      thankVideoUrl: authUser.attributes.thankVideoUrl,
      rateVideoUrl: authUser.attributes.rateVideoUrl,
      accountType: authUser.attributes.accountType,
      password: authUser.attributes.password,
      subscriptionFee: authUser.attributes.subscriptionFee,
      subscriptionFrequency: authUser.attributes.subscriptionFrequency,
      sponsors,
      achievements,
      statistics,
      badges,
      categories: authUser.categories.map((i) => i.id),
    });
  };

  React.useEffect(() => {
    methods.reset({
      firstName: authUser.firstName,
      lastName: authUser.lastName,
      email: authUser.email,
      profileImgUrl: authUser.profileImgUrl,
      bio: authUser.bio,
      category: authUser.attributes.category,
      currency: authUser.attributes.currency,
      introVideoUrl: authUser.attributes.introVideoUrl,
      thankVideoUrl: authUser.attributes.thankVideoUrl,
      rateVideoUrl: authUser.attributes.rateVideoUrl,
      accountType: authUser.attributes.accountType,
      password: authUser.attributes.password,
      subscriptionFee: authUser.attributes.subscriptionFee,
      subscriptionFrequency: authUser.attributes.subscriptionFrequency,
      sponsors,
      achievements,
      statistics,
      badges,
      categories: authUser.categories.map((i) => i.id),
    });
  }, [authUser, sponsors, achievements, statistics]);

  const onSubmit: SubmitHandler<IUpdateProfileForm> = React.useCallback((data) => {
    dispatch(editProfileActions.updateProfile(data));
    setTimeout(() => {
      const queryParams = new URLSearchParams(location.search);
      const newProfile = queryParams.get('newProfile');
      if (newProfile !== null && newProfile === 'true') {
        navigate('/profile/message');
      } else {
        navigate('/profile');
      }
    }, 1000);
  }, [dispatch]);

  const saveHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    methods.handleSubmit(onSubmit)();
  };

  const discardHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    confirm({
      variant: 'confirm',
      icon: 'warning',
      message: 'Discard all information and reset your profile setup?',
      confirmButtonTxt: 'Yes, discard and reset',
      cancelButtonTxt: 'Back to setup',
    })
      .then(() => {
        resetStateChanges();
        if (!isNewProfile) {
          navigate('/profile');
        }
      });
  };

  return (
    <>
      <div className="container">
        <StickyHeader>
          <div className="row mb-2">
            <div className="col-12 col-md-6 col-lg-7 col-xl-8 flex-column justify-content-center">
              <Typography variant="h4" sx={{ mb: 3 }}>{isNewProfile ? 'Create your profile' : 'Update Profile'}</Typography>
            </div>

            {/* desktop */}
            <div className="col-12 col-md-6 col-lg-5 col-xl-4 d-none d-md-block">
              <div className="row">
                <div className="col-md-7 mb-2 mb-md-0">
                  <PrimaryLoadingButton
                    size="large"
                    type="button"
                    sx={{ mr: 1 }}
                    onClick={saveHandler}
                    loading={loading}
                    fullWidth
                  >
                    Save & continue
                  </PrimaryLoadingButton>
                </div>
                <div className="col-md-5">
                  <SecondaryButton size="large" type="button" onClick={discardHandler} fullWidth>
                    Discard
                  </SecondaryButton>
                </div>
              </div>
            </div>

          </div>
        </StickyHeader>
        <div className="row">
          <div className="col-12 col-md-12 col-lg-3 mb-4 mb-lg-0">
            <StickySidebar
              navs={[
                {
                  to: 'general',
                  label: 'General information',
                  offset: -210,
                },
                {
                  to: 'bank',
                  label: 'Bank information',
                  offset: -210,
                },
                {
                  to: 'sponsors',
                  label: 'Sponsors',
                  offset: -210,
                },
                {
                  to: 'achievements',
                  label: 'Achievements',
                  offset: -210,
                },
                {
                  to: 'statistics',
                  label: 'Statistics',
                  offset: -210,
                },
                {
                  to: 'badges',
                  label: 'Badges and accreditations',
                  offset: -210,
                },
              ]}
            />
          </div>
          <div className="col-12 col-md-12 col-lg-9">
            <Loader loading={loading ?? false}>
              <FormProvider {...methods}>
                <Element name="general">
                  <WhiteBgCard sx={{ mb: 3 }}>
                    <GeneralInformationForm />
                  </WhiteBgCard>
                </Element>
                <Element name="bank">
                  <WhiteBgCard sx={{ mb: 3 }}>
                    <BankAccountDetailsForm />
                  </WhiteBgCard>
                </Element>
                <Element name="sponsors">
                  <WhiteBgCard sx={{ mb: 3 }}>
                    <SponsorsLogoForm />
                  </WhiteBgCard>
                </Element>
                <Element name="achievements">
                  <WhiteBgCard sx={{ mb: 3 }}>
                    <AchievementsForm />
                  </WhiteBgCard>
                </Element>
                <Element name="statistics">
                  <WhiteBgCard sx={{ mb: 3 }}>
                    <StatisticsForm />
                  </WhiteBgCard>
                </Element>
                <Element name="badges">
                  <WhiteBgCard sx={{ mb: 3 }}>
                    <BadgeForm />
                  </WhiteBgCard>
                </Element>
              </FormProvider>
            </Loader>
          </div>
        </div>
      </div>
      {/* mobile */}
      <div className="col-12 col-md-6 col-lg-5 col-xl-4 d-block d-md-none">
        <div className="row">
          <div className="col-md-7 mb-2 mb-md-0">
            <PrimaryLoadingButton
              fullWidth={false}
              size="large"
              type="button"
              sx={{ mr: 1, ml: 10 }}
              onClick={saveHandler}
            >
              Save & continue
            </PrimaryLoadingButton>
            <SecondaryButton size="large" type="button" onClick={discardHandler} fullWidth={false}>
              Discard
            </SecondaryButton>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfileUpdate;
